import chroma from 'chroma-js'

export type OKLCH = [number, number, number]

export type Override = { scale?: number; chroma?: number; lightness?: number }

export type Configuration = {
  scales: number
  chromaStepType: 'value' | 'percentage'
  chromaStep: number
  chromaMinimum: number
  overrides: Override[]
  curve: number[]
}

export type Palette = {
  base: { oklch: OKLCH; index: number }
  palette: OKLCH[]
}

// https://palette-generator.significa.co
const PROTOCOL_CONFIG: Configuration = {
  scales: 18,
  chromaStepType: 'percentage',
  chromaStep: 0.09,
  chromaMinimum: 0.02,
  overrides: [
    {
      scale: 1,
      chroma: 0.02,
    },
  ],
  curve: [
    0.992, 0.98, 0.95, 0.93, 0.91, 0.89, 0.85, 0.79, 0.71, 0.61, 0.52, 0.43,
    0.37, 0.28, 0.21, 0.14, 0.08, 0.04,
  ],
}

export function generatePalette(color: string): Palette {
  if (!chroma.valid(color)) throw new Error(`Invalid color: ${color}`)

  const {
    scales,
    chromaStepType,
    chromaStep,
    chromaMinimum,
    curve = [],
    overrides,
  } = PROTOCOL_CONFIG

  const [l, c, hue] = chroma(color).oklch()
  const h = hue || 0 // chroma returns NaN for non-hued colors (e.g. black)

  const curveArr = resizeNumberArray(curve, scales)

  const closest = findClosestNumber(curveArr, l) // find the closest number in the curve to the base color's lightness
  const shift = closest - l // how much to shift the curve to match the base color's lightness
  const index = curveArr.indexOf(closest) // index of the closest number in the curve

  const palette = Array.from(Array(scales)).map((_, i) => {
    // shift the curve to match the base color's lightness
    // don't go below 0 or above 100
    let newL = Math.min(Math.max(curveArr[i] || 0 - shift, 0), 1)

    // reduce chroma as we get further from the base color
    // don't go below the minimum (the lowest between minChroma or the base color's chroma)
    const cStep = chromaStepType === 'value' ? chromaStep : chromaStep * c
    let newC = Math.max(
      Math.min(c, chromaMinimum),
      c - cStep * Math.abs(i - index)
    )

    // overrides
    const override = overrides?.find((o) => Number(o.scale) === i + 1)

    // don't override if the step is the base color
    if (override && i !== index) {
      const c =
        override.chroma || override.chroma === 0
          ? Number(override.chroma)
          : undefined
      const l =
        override.lightness || override.lightness === 0
          ? Number(override.lightness)
          : undefined

      // chroma overrides can't be higher than what it already was
      if (c !== undefined && !isNaN(c)) newC = Math.min(newC, c)

      if (l !== undefined && !isNaN(l)) newL = l
    }

    return [newL, newC, h] as OKLCH
  })

  return { base: { oklch: [l, c, h], index: index }, palette }
}

const interpolate = (a: number, b: number, weight: number) => {
  const interpolation = (1 - weight) * a + weight * b

  return +interpolation.toFixed(8) // remove floating point errors
}

// given an array of numbers, generate a new array with a given length.
// the new numbers will be interpolated from the original array.
// so the curve is linear between any points in the original array.
export function resizeNumberArray(arr: number[], newSize: number): number[] {
  if (newSize <= 0) throw new Error('Invalid size')

  if (newSize === 1) {
    const isOdd = arr.length % 2 !== 0

    if (isOdd) {
      // if the original array has an odd number of numbers, return the middle number
      const middleIndex = Math.floor(arr.length / 2)
      return [arr[middleIndex]!]
    } else {
      // if the original array has an even number of numbers, return the average of the middle two numbers
      const middleIndex = arr.length / 2
      const leftNumber = arr[middleIndex - 1]!
      const rightNumber = arr[middleIndex]!
      return [interpolate(leftNumber!, rightNumber!, 0.5)]
    }
  }

  const originalSize = arr.length
  const step = (originalSize - 1) / (newSize - 1) // how much to step between each new number

  return Array.from({ length: newSize }, (_, index) => {
    const leftIndex = Math.floor(index * step)
    const rightIndex = Math.ceil(index * step)
    // weight is the fractional part of the index * step
    // it's used to lean more to the left or right of the pair of numbers
    const weight = index * step - leftIndex

    if (leftIndex === rightIndex) {
      // the new number is in the original array
      return arr[leftIndex]!
    } else {
      // get the pair of numbers to interpolate from
      const leftNumber = arr[leftIndex]!
      const rightNumber = arr[rightIndex]!

      return interpolate(leftNumber, rightNumber, weight)
    }
  })
}

// function that finds the closest number in an array of numbers
export function findClosestNumber(arr: number[], target: number) {
  return arr.reduce((prev, curr) => {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  })
}

const getL = (l: number) => +(l * 100).toFixed(2) + '%'
const getC = (c: number) => +c.toFixed(3)
const getH = (h: number) => +(h || 0).toFixed(2)
export const getOklchValue = (l: number, c: number, h: number) => {
  return `oklch(${getL(l)} ${getC(c)} ${getH(h)})`
}

export function getValuesFromPalette(
  palette: Palette['palette'],
  scale: number
) {
  const target = palette[scale - 1] // scale is 1-indexed

  if (!target) throw new Error('Invalid scale')

  return getOklchValue(...target)
}
